import {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {AlertTriangle, Check, Copy, ExternalLink, KeyRound, LockKeyhole} from "lucide-react";
import {PhoneHQButton, PhoneHQCheckbox, PhoneHQDialog, PhoneHQInput} from "ch3-ui-lib";
import {selectTenant, selectUserId} from "../../../store/features/Auth/authSlice";
import {
    ApiKeySummary,
    IntegrationStatusEnum,
    useGetApiKeysQuery,
} from "../../../store/features/integrations/integrationsService";
import {useFetchGroupsAdminQuery} from "../../../store/features/groups/groupsService";
import {useListEnsAlertTemplatesQuery, useUpdateEnsAlertTemplateMutation} from "../../../store/features/ens/ensService";
import {EnsAlertTemplate, EnsChannel} from "../../../store/features/ens/types";
import {ApiKeyCreationDialog} from "../../Integrations/API/ApiKeyCreationDialog";
import {formatDuration, requiresVoicePrompt} from "../CreateAlertWizard/utils";
import {buildSaveTemplateRequest} from "./templateUtils";
import {
    ApiTriggerTileState,
    buildCurlExample,
    inferEnvironmentLabel,
    normalizeApiBaseUrl,
    orderApiKeys,
    suggestedApiKeyName,
} from "./apiTriggerSetupUtils";

const STEP_LABELS = ['Review', 'API access', 'Enable', 'Instructions', 'Completion'];
const CHANNEL_LABEL: Record<EnsChannel, string> = {
    PUSH: 'Push notification',
    INTERNAL_CALL: 'ASSIST call',
    SMS: 'SMS',
    PSTN: 'Phone call',
    WHATSAPP: 'WhatsApp',
};

interface Props {
    open: boolean;
    template: EnsAlertTemplate | null;
    entryState: ApiTriggerTileState;
    onOpenChange: (open: boolean) => void;
}

const CopyBlock = ({label, value, onCopied}: {label: string; value: string; onCopied?: () => void}) => {
    const [copied, setCopied] = useState(false);
    const [failed, setFailed] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setFailed(false);
            onCopied?.();
        } catch {
            setFailed(true);
        }
    };
    return (
        <div className="rounded-lg border border-line bg-surface-muted">
            <div className="flex items-center justify-between gap-3 border-b border-line px-3 py-2">
                <span className="text-xs font-semibold text-ink-secondary">{label}</span>
                <PhoneHQButton variant="secondary" size="sm" onClick={copy}>
                    <Copy size={14}/>{copied ? 'Copied' : 'Copy'}
                </PhoneHQButton>
            </div>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all p-3 text-xs leading-5 text-ink">{value}</pre>
            {failed && <div className="px-3 pb-2 text-xs text-danger-ink">Clipboard access failed. Check browser permissions and retry.</div>}
        </div>
    );
};

export const ApiTriggerSetupWizard = ({open, template, entryState, onOpenChange}: Props) => {
    const tenant = useSelector(selectTenant) ?? '';
    const userId = useSelector(selectUserId);
    const publicBaseUrl = normalizeApiBaseUrl(
        window.__ENV__?.URL_WEBAPP_API_PUBLIC || window.__ENV__?.URL_WEBAPP_API || window.location.origin,
    );
    const phqEnvironment = inferEnvironmentLabel(publicBaseUrl);
    const [step, setStep] = useState(1);
    const [systemName, setSystemName] = useState('');
    const [externalEnvironment, setExternalEnvironment] = useState(phqEnvironment);
    const [accessMode, setAccessMode] = useState<'existing' | 'create'>('existing');
    const [selectedKeyId, setSelectedKeyId] = useState<number | null>(null);
    const [secretConfirmed, setSecretConfirmed] = useState(false);
    const [createdKey, setCreatedKey] = useState<ApiKeySummary | null>(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [workingTemplate, setWorkingTemplate] = useState<EnsAlertTemplate | null>(template);
    const [enableError, setEnableError] = useState<string | null>(null);
    const [tenantCopied, setTenantCopied] = useState(false);
    const [requestCopied, setRequestCopied] = useState(false);
    const [dedupConfirmed, setDedupConfirmed] = useState(false);

    const {data: keyResponse, isLoading: keysLoading, isError: keysError, refetch: refetchKeys} = useGetApiKeysQuery(undefined, {skip: !open});
    const {data: allTemplates = []} = useListEnsAlertTemplatesQuery(undefined, {skip: !open});
    const [updateTemplate, {isLoading: enabling}] = useUpdateEnsAlertTemplateMutation();
    const {data: groupsData} = useFetchGroupsAdminQuery(
        {userId, phrase: '', limit: 1000},
        {skip: !open || !userId},
    );

    const keys = useMemo(() => {
        const listed = keyResponse?.data ?? [];
        if (!createdKey || listed.some((key) => key.id === createdKey.id)) return listed;
        return [...listed, createdKey];
    }, [createdKey, keyResponse?.data]);
    const orderedKeys = useMemo(
        () => orderApiKeys(keys, systemName, externalEnvironment),
        [externalEnvironment, keys, systemName],
    );
    const activeKeys = keys.filter((key) => key.status === IntegrationStatusEnum.ENABLED);
    const selectedKey = keys.find((key) => key.id === selectedKeyId) ?? null;
    const keyReady = selectedKey?.status === IntegrationStatusEnum.ENABLED && secretConfirmed;
    const voicePromptMissingForApi = workingTemplate != null
        && requiresVoicePrompt(workingTemplate.levels)
        && workingTemplate.voicePrompt == null;

    useEffect(() => {
        if (!open || !template) return;
        setWorkingTemplate(template);
        setStep(entryState === 'off' ? 1 : entryState === 'needs-key' ? 2 : 4);
        setSystemName('');
        setExternalEnvironment(phqEnvironment);
        setAccessMode('existing');
        setSelectedKeyId(null);
        setSecretConfirmed(false);
        setCreatedKey(null);
        setEnableError(null);
        setTenantCopied(false);
        setRequestCopied(false);
        setDedupConfirmed(false);
    }, [entryState, open, phqEnvironment, template]);

    useEffect(() => {
        if (open && !keysLoading && activeKeys.length === 0 && selectedKeyId == null) setAccessMode('create');
    }, [activeKeys.length, keysLoading, open, selectedKeyId]);

    if (!template || template.id == null || template.system) return null;

    const groupNames = new Map((groupsData?.groups ?? []).map(({group}) => [group.groupId, group.name]));
    const directRecipients = template.targetUsers.map((user) =>
        `${user.firstName} ${user.lastName}`.trim() || user.email,
    );
    const enabledTemplates = allTemplates
        .filter((item) => !item.system && item.id != null && item.apiTriggerEnabled)
        .map((item) => item.id === workingTemplate?.id ? workingTemplate : item)
        .filter((item): item is EnsAlertTemplate => item != null);
    if (workingTemplate?.apiTriggerEnabled && !enabledTemplates.some((item) => item.id === workingTemplate.id)) {
        enabledTemplates.push(workingTemplate);
    }
    const curlExample = buildCurlExample({baseUrl: publicBaseUrl, tenant, templateId: template.id});
    const requestBody = JSON.stringify({
        templateId: template.id,
        dedupKey: 'monitoring:incident-456',
        additionalMessage: 'Detected by the boiler-room monitor',
    }, null, 2);
    const headers = `Content-Type: application/json\nX-API-Client: ${tenant}\nX-API-Key: <read-from-secret-manager>`;
    const postman = `X-API-Client: {{phonehq_tenant}}\nX-API-Key: {{vault:phonehq-api-key}}`;

    const enable = async () => {
        if (!workingTemplate || workingTemplate.id == null) return;
        setEnableError(null);
        try {
            const next = {...workingTemplate, apiTriggerEnabled: true};
            const saved = await updateTemplate({id: workingTemplate.id, body: buildSaveTemplateRequest(next)}).unwrap();
            setWorkingTemplate(saved);
            setStep(4);
        } catch {
            setEnableError('The template could not be enabled. Nothing else was rolled back; retry when ready.');
        }
    };

    const chooseExistingKey = (key: ApiKeySummary, checked: boolean | 'indeterminate') => {
        if (key.status !== IntegrationStatusEnum.ENABLED) return;
        if (checked === true) {
            setSelectedKeyId(key.id);
            setSecretConfirmed(true);
        } else if (selectedKeyId === key.id) {
            setSelectedKeyId(null);
            setSecretConfirmed(false);
        }
    };

    const goBack = () => {
        if (step === 1) {
            onOpenChange(false);
        } else if (step === 4 && workingTemplate?.apiTriggerEnabled) {
            setStep(2);
        } else {
            setStep(step - 1);
        }
    };

    const footer = (
        <div className="flex items-center justify-between gap-3 border-t border-line px-6 py-4">
            <PhoneHQButton variant="secondary" onClick={goBack}>
                {step > 1 ? 'Back' : 'Close'}
            </PhoneHQButton>
            {step === 1 && <PhoneHQButton variant="primary" onClick={() => setStep(2)}>Prepare API access</PhoneHQButton>}
            {step === 2 && (
                <PhoneHQButton
                    variant="primary"
                    disabled={!keyReady}
                    onClick={() => setStep(workingTemplate?.apiTriggerEnabled ? 4 : 3)}
                >
                    {workingTemplate?.apiTriggerEnabled ? 'Show instructions' : 'Review enablement'}
                </PhoneHQButton>
            )}
            {step === 3 && (
                <PhoneHQButton variant="primary" disabled={enabling || voicePromptMissingForApi} onClick={enable}>
                    {enabling ? 'Enabling…' : 'Enable API trigger'}
                </PhoneHQButton>
            )}
            {step === 4 && <PhoneHQButton variant="primary" onClick={() => setStep(5)}>Completion checklist</PhoneHQButton>}
            {step === 5 && <PhoneHQButton variant="primary" onClick={() => onOpenChange(false)}>Close</PhoneHQButton>}
        </div>
    );

    return (
        <>
            <PhoneHQDialog
                open={open}
                onOpenChange={onOpenChange}
                title={`API trigger setup · ${template.name}`}
                theme="light"
                size="wide"
                noPadding
            >
                <div className="flex max-h-[82vh] flex-col">
                    <ol className="grid grid-cols-5 border-y border-line bg-surface-muted px-6 py-3">
                        {STEP_LABELS.map((label, index) => {
                            const number = index + 1;
                            return (
                                <li key={label} aria-current={step === number ? 'step' : undefined} className="flex items-center gap-2 text-xs font-semibold">
                                    <span className={`flex h-6 w-6 items-center justify-center rounded-full ${step >= number ? 'bg-brand text-white' : 'bg-fill-subtle text-ink-secondary'}`}>
                                        {step > number ? <Check size={14}/> : number}
                                    </span>
                                    <span className="hidden text-ink-secondary sm:inline">{label}</span>
                                </li>
                            );
                        })}
                    </ol>

                    <div className="flex-1 overflow-y-auto px-6 py-5">
                        {step === 1 && (
                            <div className="flex flex-col gap-5">
                                <div>
                                    <h3 className="text-base font-bold text-ink">Review the real alert configuration</h3>
                                    <p className="mt-1 text-sm text-ink-muted">No template change is made on this step.</p>
                                </div>
                                <dl className="grid gap-3 rounded-lg border border-line p-4 sm:grid-cols-2">
                                    <div><dt className="text-xs text-ink-muted">Template</dt><dd className="mt-1 text-sm font-semibold">{template.name} · ID {template.id}</dd></div>
                                    <div><dt className="text-xs text-ink-muted">PHQ environment</dt><dd className="mt-1 text-sm font-semibold">{phqEnvironment} · {publicBaseUrl}</dd></div>
                                    <div><dt className="text-xs text-ink-muted">Target groups</dt><dd className="mt-1 text-sm">{template.targetGroupIds.length ? template.targetGroupIds.map((id) => groupNames.get(id) ?? `Group #${id}`).join(', ') : 'None'}</dd></div>
                                    <div><dt className="text-xs text-ink-muted">Individuals</dt><dd className="mt-1 text-sm">{directRecipients.length ? directRecipients.join(', ') : 'None'}</dd></div>
                                </dl>
                                <div className="rounded-lg border border-line p-4">
                                    <div className="mb-3 text-sm font-semibold">Escalation</div>
                                    <div className="flex flex-col gap-2">
                                        {[...template.levels].sort((a, b) => a.sequenceNo - b.sequenceNo).map((level, index) => (
                                            <div key={level.sequenceNo} className="flex items-center gap-3 text-sm">
                                                <span className="font-semibold text-brand-ink">L{level.sequenceNo}</span>
                                                <span>{CHANNEL_LABEL[level.channel]}</span>
                                                <span className="text-ink-muted">{index === 0 ? 'Immediately' : `${formatDuration(level.delaySecondsFromPrevious)} after previous level`}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 rounded-lg border border-danger-tint bg-danger-tint p-4 text-sm leading-6 text-danger-ink">
                                    <AlertTriangle size={20} className="mt-0.5 shrink-0"/>
                                    A successful API request creates a real alert and starts contacting the recipients shown above. This wizard will not send a test request.
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="flex flex-col gap-5">
                                <div>
                                    <h3 className="text-base font-bold text-ink">Prepare tenant API access</h3>
                                    <p className="mt-1 text-sm text-ink-muted">Describe the caller so PHQ can recommend a reusable integration/environment key.</p>
                                </div>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <PhoneHQInput id="ensApi-system" label="External system" placeholder="Datadog" value={systemName} onChange={(event) => setSystemName(event.target.value)}/>
                                    <PhoneHQInput id="ensApi-environment" label="External system environment" placeholder="production" value={externalEnvironment} onChange={(event) => setExternalEnvironment(event.target.value)}/>
                                </div>
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                                    <div className="flex items-center gap-2 font-semibold"><LockKeyhole size={17}/>Tenant-wide credential</div>
                                    <p className="mt-1">Every active key can trigger every API-enabled ENS template. It can also access API_CLIENT-authorized contact and webhook APIs. Names, expiry, scopes, template allowlists, last-used metadata, and overlapping rotation are not enforced by the current backend.</p>
                                </div>
                                <fieldset className="flex gap-3">
                                    <legend className="mb-2 text-sm font-semibold">Does the caller already have a suitable integration key?</legend>
                                    <label className="flex items-center gap-2 text-sm"><input type="radio" name="api-access-mode" checked={accessMode === 'existing'} onChange={() => setAccessMode('existing')}/>Yes, use an existing key</label>
                                    <label className="flex items-center gap-2 text-sm"><input type="radio" name="api-access-mode" checked={accessMode === 'create'} onChange={() => setAccessMode('create')}/>No, create one</label>
                                </fieldset>

                                {keysLoading && <div className="text-sm text-ink-muted">Loading API keys…</div>}
                                {keysError && (
                                    <div className="flex items-center justify-between rounded-lg border border-danger-tint bg-danger-tint p-3 text-sm text-danger-ink">
                                        API keys could not be loaded.
                                        <PhoneHQButton variant="secondary" size="sm" onClick={() => refetchKeys()}>Retry</PhoneHQButton>
                                    </div>
                                )}
                                {!keysLoading && !keysError && accessMode === 'existing' && (
                                    <div className="flex flex-col gap-2">
                                        <div className="rounded-lg border border-line bg-surface-muted p-3 text-xs leading-5 text-ink-secondary">
                                            Existing secrets cannot be revealed or copied again. If a secret was lost, create a replacement and disable or delete the unused key in Admin → Integrations → API.
                                        </div>
                                        {orderedKeys.length === 0 && <div className="rounded-lg border border-line p-4 text-sm text-ink-muted">No API keys exist. Choose “No, create one”.</div>}
                                        {orderedKeys.map((key) => {
                                            const enabled = key.status === IntegrationStatusEnum.ENABLED;
                                            const checked = selectedKeyId === key.id && secretConfirmed;
                                            return (
                                                <div key={key.id} className={`rounded-lg border p-3 ${checked ? 'border-brand-focus bg-brand-tint-subtle' : 'border-line'}`}>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="flex min-w-0 items-center gap-3">
                                                            <KeyRound size={18} className="shrink-0 text-brand-ink"/>
                                                            <div className="min-w-0"><div className="truncate text-sm font-semibold">{key.name}</div><div className="text-xs text-ink-muted">••••••{key.apiKeyShortcut}</div></div>
                                                        </div>
                                                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${enabled ? 'bg-success-tint text-success-ink' : 'bg-surface-muted text-ink-secondary'}`}>{enabled ? 'Enabled' : 'Disabled'}</span>
                                                    </div>
                                                    {enabled ? (
                                                        <div className="mt-3 border-t border-line pt-3">
                                                            <PhoneHQCheckbox checked={checked} onCheckedChange={(value) => chooseExistingKey(key, value)} label="Use this key; I confirm its secret is stored externally"/>
                                                        </div>
                                                    ) : <div className="mt-2 text-xs text-ink-muted">Disabled keys cannot be selected. Re-enable it in Admin → Integrations → API or create a replacement.</div>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {accessMode === 'create' && (
                                    <div className="rounded-lg border border-line p-4">
                                        <div className="text-sm font-semibold">Recommended name</div>
                                        <div className="mt-1 font-mono text-sm text-brand-ink">{suggestedApiKeyName(systemName || '<system>', externalEnvironment || '<environment>')}</div>
                                        <p className="mt-2 text-xs leading-5 text-ink-muted">Reuse this key for the same external integration and environment across multiple templates. It is not a “template key”.</p>
                                        <PhoneHQButton className="mt-3" variant="primary" disabled={!systemName.trim() || !externalEnvironment.trim()} onClick={() => setCreateDialogOpen(true)}>Create integration key</PhoneHQButton>
                                    </div>
                                )}
                                {keyReady && <div className="rounded-lg border border-success-tint bg-success-tint p-3 text-sm text-success-ink">Selected {selectedKey?.name}; possession of its one-time secret is confirmed.</div>}
                            </div>
                        )}

                        {step === 3 && (
                            <div className="flex flex-col gap-5">
                                <div><h3 className="text-base font-bold">Enable API triggering</h3><p className="mt-1 text-sm text-ink-muted">This is the only setup step that changes the template.</p></div>
                                <div className="rounded-lg border border-line bg-surface-muted p-5 font-mono text-sm">apiTriggerEnabled: <span className="text-danger-ink">false</span> → <span className="text-success-ink">true</span></div>
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">The full current template DTO will be saved. Once enabled, all active tenant API keys—not only the selected key—can trigger it.</div>
                                {voicePromptMissingForApi && (
                                    <div className="rounded-lg border border-danger-tint bg-danger-tint p-4 text-sm leading-6 text-danger-ink">
                                        This template uses phone or internal-call escalation but has no voice prompt. Edit the template and generate a reusable prompt before enabling API triggers, because API requests bypass the Create alert wizard.
                                    </div>
                                )}
                                {enableError && <div className="rounded-lg border border-danger-tint bg-danger-tint p-3 text-sm text-danger-ink">{enableError}</div>}
                            </div>
                        )}

                        {step === 4 && (
                            <div className="flex flex-col gap-5">
                                <div><h3 className="text-base font-bold">Integration instructions</h3><p className="mt-1 text-sm text-ink-muted">Supply the secret from an external secret manager. PHQ never inserts it into generated code.</p></div>
                                {!keyReady && (
                                    <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                                        An active key may exist, but possession of its one-time secret is not confirmed in this wizard.
                                        <PhoneHQButton variant="secondary" size="sm" onClick={() => setStep(2)}>Confirm API access</PhoneHQButton>
                                    </div>
                                )}
                                {voicePromptMissingForApi && (
                                    <div className="rounded-lg border border-danger-tint bg-danger-tint p-3 text-sm leading-6 text-danger-ink">
                                        Voice-call delivery is not fully configured for this API-enabled template. Add a reusable voice prompt in the template editor.
                                    </div>
                                )}
                                <div className="grid gap-3 sm:grid-cols-3">
                                    <CopyBlock label="Backend base URL" value={publicBaseUrl}/>
                                    <CopyBlock label="X-API-Client tenant" value={tenant} onCopied={() => setTenantCopied(true)}/>
                                    <CopyBlock label="Numeric template ID" value={String(template.id)}/>
                                </div>
                                <CopyBlock label="Request headers" value={headers}/>
                                <CopyBlock label="Request body" value={requestBody}/>
                                <div className="rounded-lg border border-brand-tint bg-brand-tint-subtle p-3 text-sm leading-6 text-brand-ink">
                                    <code>dedupKey</code> is optional but strongly recommended. Send a stable upstream incident identifier of at most 255 characters. <code>additionalMessage</code> is optional and limited to 500 characters.
                                </div>
                                <CopyBlock label="Safe curl example" value={curlExample} onCopied={() => setRequestCopied(true)}/>
                                <CopyBlock label="Postman Vault headers" value={postman}/>
                                <div className="rounded-lg border border-line p-4">
                                    <h4 className="text-sm font-semibold">Response meanings</h4>
                                    <dl className="mt-3 grid gap-2 text-sm">
                                        <div><dt className="inline font-semibold">202 Accepted — </dt><dd className="inline">a new alert was created or an open alert was returned with <code>deduplicated: true</code>. Non-empty <code>skippedRecipients</code> is a successful-trigger warning.</dd></div>
                                        <div><dt className="inline font-semibold">400 Bad Request — </dt><dd className="inline">the payload is invalid, the deduplication key exceeds 255 characters, or the additional message exceeds 500 characters.</dd></div>
                                        <div><dt className="inline font-semibold">401 Unauthorized — </dt><dd className="inline">the tenant identifier or API-key secret is missing, invalid, mismatched, or disabled.</dd></div>
                                        <div><dt className="inline font-semibold">409 Conflict — </dt><dd className="inline">the template is not enabled for API triggering or conflicts with current server state.</dd></div>
                                        <div><dt className="inline font-semibold">429 Too Many Requests — </dt><dd className="inline">rate limited; honor <code>Retry-After</code> and retry with the same stable deduplication key.</dd></div>
                                    </dl>
                                </div>
                                <div className="rounded-lg border border-line p-4 text-sm leading-6 text-ink-secondary">
                                    <div className="font-semibold">Credential reach</div>
                                    <p>Current enabled ENS templates accessible to any active key:</p>
                                    <ul className="list-disc pl-5">{enabledTemplates.map((item) => <li key={item.id}>{item.name} · ID {item.id}</li>)}</ul>
                                    <a className="mt-2 inline-flex items-center gap-1 font-semibold text-brand-ink" href={`${publicBaseUrl}/swagger-ui/#/`} target="_blank" rel="noreferrer">Review all API_CLIENT endpoints in Swagger <ExternalLink size={14}/></a>
                                </div>
                            </div>
                        )}

                        {step === 5 && (
                            <div className="flex flex-col gap-4">
                                <div><h3 className="text-base font-bold">Completion checklist</h3><p className="mt-1 text-sm text-ink-muted">This checklist is guidance for this visit; PHQ does not persist a Connected or Ready status.</p></div>
                                {[
                                    [workingTemplate?.apiTriggerEnabled === true, 'Template enabled'],
                                    [selectedKey?.status === IntegrationStatusEnum.ENABLED, 'Active integration/environment key selected or created'],
                                    [secretConfirmed, 'Possession of its one-time secret confirmed in an external secret manager'],
                                    [tenantCopied, 'Tenant identifier copied'],
                                    [requestCopied, 'Request example copied'],
                                ].map(([done, label]) => (
                                    <div key={String(label)} className="flex items-center gap-3 text-sm"><span className={`flex h-6 w-6 items-center justify-center rounded-full ${done ? 'bg-success-tint text-success-ink' : 'bg-surface-muted text-ink-muted'}`}>{done ? <Check size={15}/> : '–'}</span>{label}</div>
                                ))}
                                <div className="rounded-lg border border-line p-3">
                                    <PhoneHQCheckbox checked={dedupConfirmed} onCheckedChange={(checked) => setDedupConfirmed(checked === true)} label="The upstream system is configured to send a stable deduplication key"/>
                                </div>
                                {workingTemplate?.apiTriggerEnabled && keyReady && tenantCopied && requestCopied && dedupConfirmed && (
                                    <div className="rounded-lg border border-success-tint bg-success-tint p-3 text-sm text-success-ink">All operator checks for this setup visit are complete.</div>
                                )}
                            </div>
                        )}
                    </div>
                    {footer}
                </div>
            </PhoneHQDialog>
            <ApiKeyCreationDialog
                open={createDialogOpen}
                suggestedName={suggestedApiKeyName(systemName, externalEnvironment)}
                onOpenChange={setCreateDialogOpen}
                onStored={(key) => {
                    setCreatedKey(key);
                    setSelectedKeyId(key.id);
                    setSecretConfirmed(true);
                    setAccessMode('existing');
                }}
            />
        </>
    );
};
